// @ts-nocheck
/**
 * Birch Registry — gift-giver landing page.
 * Elegant public view rendered from /apps/registry/api/public/:slug
 * Mount point: <div id="rgl-root" data-slug="...">
 */
(function () {
  // Service-fee feature flag (mirror of the worker's SERVICE_FEE_ENABLED and
  // registry-store.js). When false, no 3% fee is shown or passed to the
  // contribution modal, regardless of what the pool payload carries.
  const SERVICE_FEE_ENABLED = false;
  // ── State ─────────────────────────────────────────────────────────────────
  // Declared BEFORE the ready/init scheduler so `init()` doesn't hit the
  // temporal dead zone when the document is already past the loading state
  // (in which case `ready()` runs synchronously and calls `init()` before
  // the rest of the IIFE has executed).
  const state = {
    registry: null,
    filter: "still_needed", // "all" | "still_needed" | "purchased"
    sort: "priority",       // "priority" | "price_asc" | "price_desc"
    shipToSelf: false,      // false = ship to registrant, true = ship to giver
    funds: [],              // cash funds loaded async after initial render
    // Identity defaults sourced from Liquid (customer.* if signed in) — used
    // to prefill the giver modal so repeat purchases don't retype name/email.
    giverName:  "",
    giverEmail: "",
    // Unified gift cart, set in init() once the slug is known. `cartUI` is the
    // shared drawer/checkout controller (registry-store.js giftCartUI); `cart`
    // is its underlying persisted store, used for "In cart ✓" label checks.
    cartUI: null,
    cart: null,
  };

  const ready = () => {
    if (!window.BirchRegistry) return setTimeout(ready, 30);
    const root = document.getElementById("rgl-root");
    if (root) init(root);
  };
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", ready);
  else ready();

  // ── Init ──────────────────────────────────────────────────────────────────
  async function init(root) {
    const { API } = window.BirchRegistry;
    const slug = root.dataset.slug || new URLSearchParams(location.search).get("slug");
    if (!slug) { root.innerHTML = renderError("Registry not found."); return; }

    state.giverName  = root.dataset.giverName  || "";
    state.giverEmail = root.dataset.giverEmail || "";

    root.innerHTML = renderLoading();

    let payload;
    try {
      payload = await API.publicView(slug);
    } catch (e) {
      // Private registry: the public read returns 401 + { gated:true, registry }
      // (display name + image only). Show a password gate; a correct password
      // sets the unlock cookie and we re-init to load the real registry.
      if (e.status === 401 && e.body && e.body.gated) {
        renderPasswordGate(root, slug, e.body.registry || {});
        return;
      }
      root.innerHTML = renderError(
        e.status === 404 ? "This registry isn't available or hasn't been shared yet." : "Something went wrong. Please try again."
      );
      return;
    }

    state.registry = {
      ...payload.registry,
      items: (payload.registry.items || []).map((it) => ({
        ...it,
        // Items the parent marked as done (acquired outside the registry) are
        // treated as already taken care of, so gift-givers don't see them under
        // "still needed". The row remains visible on the parent's edit page.
        stillNeeded: it.decisionState === "fulfilled"
          ? 0
          : Math.max(0, (it.quantityWanted || 1) - (it.quantityPurchased || 0)),
      })),
    };

    // Record a single view per browser, keyed by slug. Reloading the page
    // doesn't bump the owner's "Visits So Far" again. Fire-and-forget — a
    // failed record shouldn't block the public view from rendering.
    const channel = new URLSearchParams(location.search).get("c") || null;
    API.recordView(slug, channel);

    // Shared unified gift cart (drawer + FAB + checkout) — see registry-store.js.
    state.cartUI = window.BirchRegistry.giftCartUI({
      slug,
      getRegistry: () => state.registry,
      getItem: (id) => (state.registry?.items || []).find((x) => x.id === id),
      onChange: () => render(root),          // re-paint grid so "In cart ✓" stays accurate
      defaultShipSelf: () => state.shipToSelf, // mirror the page-level ship-to toggle
    });
    state.cart = state.cartUI.cart;

    render(root);
    loadAndRenderFunds(state.registry.id);
  }

  // ── Cash Funds: load and render for gift-giver ────────────────────────────
  function loadAndRenderFunds(registryId) {
    if (!registryId) return;
    const cfg = window.BirchCashFundsConfig || {};
    const proxy = cfg.aiProxy || "/apps/ai";
    fetch(proxy + "/cash-funds/public?registry_id=" + encodeURIComponent(registryId), { credentials: "same-origin" })
      .then(function(r){ return r.json(); })
      .then(function(res){
        if (res.funds && res.funds.length) {
          state.funds = res.funds;
          // Patch tab counts without a full re-render (which would wipe the funds section)
          var allItemsLen = (state.registry && state.registry.items || []).length;
          var allTab = document.querySelector("[data-filter='all']");
          if (allTab) allTab.textContent = "All Items (" + (allItemsLen + res.funds.length) + ")";
          var fundsTab = document.querySelector("[data-filter='funds']");
          if (fundsTab) { fundsTab.textContent = "Funds (" + res.funds.length + ")"; fundsTab.style.display = ""; }
          renderFundsSection(res.funds, proxy, cfg.stripePk);
        }
      })
      .catch(function(){});
  }

  // Stripe publishable key — fetched once, shared across fund sections
  var _stripePkCache = null;
  function getStripePk(proxy, initial, cb) {
    var valid = function(k) { return k && k.indexOf("pk_") === 0 && k !== "pk_test_placeholder" && k !== "pk_live_placeholder"; };
    if (_stripePkCache) { cb(_stripePkCache); return; }
    if (valid(initial)) { _stripePkCache = initial; cb(initial); return; }
    fetch(proxy + "/cash-funds/stripe-config", { credentials: "same-origin" })
      .then(function(r) { return r.json(); })
      .then(function(res) { if (valid(res.publishable_key)) { _stripePkCache = res.publishable_key; cb(res.publishable_key); } else { cb(null); } })
      .catch(function() { cb(null); });
  }

  function renderFundsSection(funds, proxy, stripePk) {
    const section = document.getElementById("rgl-funds-section");
    if (!section) return;
    const esc = window.BirchRegistry.escapeHtml;
    section.style.display = "";
    // Pre-warm the key cache
    getStripePk(proxy, stripePk, function() {});

    const FUND_TYPE_ICONS = { diaper: '🍼', takeout: '🍕', general: '🌿', custom: '✦' };

    section.innerHTML = `
      <div class="rgl-funds-wrap">
        <div class="rgl-funds-header">
          <div class="rgl-funds-eyebrow">Some gifts aren't things</div>
          <h2 class="rgl-funds-title">Contribute to a fund</h2>
          <p class="rgl-funds-sub">All of your gift reaches the family.</p>
        </div>
        <div class="rgl-funds-grid">
          ${funds.map(function(f){
            const raised = (f.total_raised_cents || 0) / 100;
            const goal   = f.goal_amount_cents ? f.goal_amount_cents / 100 : null;
            const pct    = goal ? Math.min(100, Math.round((raised / goal) * 100)) : null;
            const tiers  = JSON.parse(f.suggested_tiers || "[1500,3000,5000]");
            const icon   = FUND_TYPE_ICONS[f.type] || '✦';
            return `<div class="rgl-fund-card">
              <div class="rgl-fund-card__header">
                <span class="rgl-fund-card__type-icon">${icon}</span>
                <div class="rgl-fund-card__name">${esc(f.name)}</div>
              </div>
              ${goal ? `<div class="rgl-fund-card__bar-wrap"><div class="rgl-fund-card__bar" style="width:${pct}%"></div></div>
                <p class="rgl-fund-card__raised">$${raised.toFixed(2)} gifted${goal ? " of $" + goal.toFixed(2) : ""} · ${f.contributor_count || 0} contributor${f.contributor_count === 1 ? "" : "s"}</p>` : ""}
              <div class="rgl-fund-tiers">
                ${tiers.map(function(t){ return `<button class="rgl-fund-tier-btn" data-fund="${esc(f.id)}" data-amount="${t}">$${t/100}</button>`; }).join("")}
              </div>
              <button class="rgl-fund-contribute-btn" data-fund-id="${esc(f.id)}" data-fund-name="${esc(f.name)}">Contribute →</button>
            </div>`;
          }).join("")}
        </div>
      </div>

      <!-- Contribution modal -->
      <div id="rgl-fund-modal" class="rgl-fund-modal" hidden>
        <div class="rgl-fund-modal__box">
          <button class="rgl-fund-modal__close" id="rgl-fund-modal-close">&times;</button>
          <h3 class="rgl-fund-modal__title" id="rgl-fund-modal-title">Contribute</h3>

          <!-- Amount -->
          <label class="rgl-fund-modal__label">Amount</label>
          <div class="rgl-fund-modal__amount-row" id="rgl-fund-amount-row">
            <span class="rgl-fund-modal__dollar">$</span>
            <input type="number" id="rgl-fund-custom-amt" class="rgl-fund-modal__amount-input" min="1" step="1" placeholder="Enter amount">
          </div>

          <!-- Details -->
          <input type="text"  class="rgl-fund-modal__input" id="rgl-fund-name"  placeholder="Your name (optional)">
          <input type="email" class="rgl-fund-modal__input" id="rgl-fund-email" placeholder="Email for receipt" required>
          <input type="text"  class="rgl-fund-modal__input" id="rgl-fund-note"  placeholder="Leave a note (optional)">

          <!-- Payment method toggle -->
          <div class="rgl-pay-methods" id="rgl-pay-methods">
            <button type="button" class="rgl-pay-method rgl-pay-method--active" id="rgl-pm-ach">
              <span class="rgl-pay-method__icon">🏦</span>
              <span><span class="rgl-pay-method__label">Bank transfer</span><span class="rgl-pay-method__hint"> — fee absorbed by Birch</span></span>
            </button>
            <button type="button" class="rgl-pay-method" id="rgl-pm-card">
              <span class="rgl-pay-method__icon">💳</span>
              <span class="rgl-pay-method__label">Credit / debit card</span>
            </button>
          </div>
          <div id="rgl-card-element-wrap" style="display:none;margin:12px 0"></div>

          <!-- Status / net -->
          <div id="rgl-fund-msg" class="rgl-fund-modal__net" style="display:none"></div>

          <!-- Submit — always visible -->
          <button class="rgl-fund-modal__submit" id="rgl-fund-submit" style="display:block;width:100%;padding:13px;background:#234637;color:#fff;border:none;border-radius:8px;font-size:0.9rem;font-weight:700;cursor:pointer;margin-top:12px">Send gift by bank transfer</button>
          <p class="rgl-fund-modal__fee-note">ACH bank transfer fee (0.8%, max $5) absorbed by Birch — $0 extra for you.</p>

          <!-- Success -->
          <div id="rgl-fund-success" style="display:none" class="rgl-fund-modal__success">
            <button id="rgl-fund-success-close" style="position:absolute;top:14px;right:14px;background:none;border:none;cursor:pointer;font-size:1.3rem;color:#7a7060;line-height:1;padding:0">&times;</button>
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4a6b4a" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/></svg>
            <p>Thank you. Your gift is now part of their story.</p>
          </div>
        </div>
      </div>`;

    // ── Tier chip pre-fills the amount input ─────────────────────────────────
    section.querySelectorAll(".rgl-fund-tier-btn").forEach(function(btn) {
      btn.addEventListener("click", function() {
        section.querySelectorAll(".rgl-fund-tier-btn").forEach(function(b){ b.classList.remove("rgl-fund-tier-btn--active"); });
        btn.classList.add("rgl-fund-tier-btn--active");
        var input = section.querySelector("#rgl-fund-custom-amt");
        if (input) input.value = (parseInt(btn.getAttribute("data-amount")) / 100).toFixed(0);
      });
    });

    // ── Open modal + wire all internal handlers at open time ─────────────────
    section.querySelectorAll(".rgl-fund-contribute-btn").forEach(function(btn) {
      btn.addEventListener("click", function() {
        var modal   = section.querySelector("#rgl-fund-modal");
        var fundId  = btn.getAttribute("data-fund-id");
        var fundName= btn.getAttribute("data-fund-name");

        // Reset modal contents
        section.querySelector("#rgl-fund-modal-title").textContent = "Contribute to " + fundName;
        var amtInput = section.querySelector("#rgl-fund-custom-amt");
        var card = btn.closest(".rgl-fund-card");
        var activeChip = card && card.querySelector(".rgl-fund-tier-btn--active");
        if (amtInput) amtInput.value = activeChip ? (parseInt(activeChip.getAttribute("data-amount")) / 100).toFixed(0) : "";
        section.querySelector("#rgl-fund-success").style.display = "none";
        section.querySelector("#rgl-fund-msg").style.display = "none";
        section.querySelector("#rgl-fund-modal-close").style.display = "";
        var payMethods = section.querySelector("#rgl-pay-methods");
        if (payMethods) payMethods.style.display = "";
        var cardWrap = section.querySelector("#rgl-card-element-wrap");
        if (cardWrap) cardWrap.style.display = "none";
        var submitEl = section.querySelector("#rgl-fund-submit");
        if (submitEl) { submitEl.style.display = ""; submitEl.disabled = false; submitEl.textContent = "Send gift by bank transfer"; }

        // Pre-fill name + email from logged-in customer profile
        var nameInput  = section.querySelector("#rgl-fund-name");
        var emailInput = section.querySelector("#rgl-fund-email");
        if (nameInput  && !nameInput.value  && state.giverName)  nameInput.value  = state.giverName;
        if (emailInput && !emailInput.value && state.giverEmail) emailInput.value = state.giverEmail;

        // Payment method toggle — rewire each open
        var useCard = false;
        var cardElRef = null;
        var pmAch  = section.querySelector("#rgl-pm-ach");
        var pmCard = section.querySelector("#rgl-pm-card");
        if (pmAch)  { pmAch.classList.add("rgl-pay-method--active"); pmCard && pmCard.classList.remove("rgl-pay-method--active"); }

        if (pmAch) pmAch.onclick = function() {
          useCard = false;
          pmAch.classList.add("rgl-pay-method--active");
          if (pmCard) pmCard.classList.remove("rgl-pay-method--active");
          if (cardWrap) cardWrap.style.display = "none";
          if (submitEl) submitEl.textContent = "Send gift by bank transfer";
        };
        if (pmCard) pmCard.onclick = function() {
          useCard = true;
          pmCard.classList.add("rgl-pay-method--active");
          pmAch.classList.remove("rgl-pay-method--active");
          if (cardWrap) cardWrap.style.display = "";
          if (submitEl) submitEl.textContent = "Pay by card";
          if (!cardElRef) {
            getStripePk(proxy, stripePk, function(pk) {
              if (!pk) return;
              try {
                var stripeInst = Stripe(pk);
                var els = stripeInst.elements({ appearance: { theme: "stripe" } });
                cardElRef = els.create("card");
                cardElRef.mount(cardWrap);
              } catch(e) { console.warn("[stripe card mount]", e); }
            });
          }
        };

        // Submit handler
        if (submitEl) submitEl.onclick = function() {
          var amountCents = amtInput ? Math.round(parseFloat(amtInput.value) * 100) : 0;
          var msgEl = section.querySelector("#rgl-fund-msg");

          function showErr(txt) {
            submitEl.disabled = false;
            submitEl.textContent = useCard ? "Pay by card" : "Send gift by bank transfer";
            if (msgEl) { msgEl.style.display = ""; msgEl.style.color = "#b05050"; msgEl.textContent = txt; }
          }

          if (!amountCents || amountCents < 100) {
            if (msgEl) { msgEl.style.display = ""; msgEl.style.color = "#b05050"; msgEl.textContent = "Please enter an amount of at least $1."; }
            if (amtInput) amtInput.focus();
            return;
          }

          var giverEmail = (section.querySelector("#rgl-fund-email") || {}).value || "";
          if (!giverEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(giverEmail)) {
            if (msgEl) { msgEl.style.display = ""; msgEl.style.color = "#b05050"; msgEl.textContent = "Please enter a valid email address to receive your receipt."; }
            var emailEl = section.querySelector("#rgl-fund-email");
            if (emailEl) emailEl.focus();
            return;
          }

          submitEl.disabled = true;
          submitEl.textContent = "Preparing…";
          if (msgEl) msgEl.style.display = "none";

          var giverName = (section.querySelector("#rgl-fund-name") || {}).value || "";
          // giverEmail already captured + validated above
          var note      = (section.querySelector("#rgl-fund-note") || {}).value || "";

          // Step 1: create PaymentIntent
          fetch(proxy + "/cash-funds/" + fundId + "/intent", {
            method: "POST", credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ amount_cents: amountCents, giver_name: giverName || null, giver_email: giverEmail || null, note: note || null }),
          }).then(function(r) { return r.json(); }).then(function(res) {
            if (!res.ok || !res.client_secret) { showErr(res.message || "Could not set up payment."); return; }

            if (msgEl) { msgEl.style.display = ""; msgEl.style.color = "#4a6b4a"; msgEl.textContent = "The family receives the full $" + ((res.net_to_family_cents || amountCents) / 100).toFixed(2) + "."; }

            // Step 2: init Stripe and invoke payment
            getStripePk(proxy, stripePk, function(pk) {
              if (!pk) { showErr("Payment gateway not configured. Please contact the registry owner."); return; }
              var stripe;
              try { stripe = Stripe(pk); } catch(e) { showErr("Could not load payment library. Please refresh."); return; }

              if (useCard && cardElRef) {
                submitEl.textContent = "Processing card…";
                stripe.confirmCardPayment(res.client_secret, {
                  payment_method: { card: cardElRef, billing_details: { name: giverName || undefined } },
                }).then(function(r) {
                  if (r.error) showErr(r.error.message); else showSuccess();
                });
              } else {
                // ACH via Stripe Financial Connections
                submitEl.textContent = "Connect your bank…";
                stripe.collectBankAccountForPayment({
                  clientSecret: res.client_secret,
                  params: {
                    payment_method_type: "us_bank_account",
                    payment_method_data: { billing_details: { name: giverName || "Gift giver", email: giverEmail || undefined } },
                  },
                }).then(function(r) {
                  if (r.error) { showErr(r.error.message); return; }
                  if (!r.paymentIntent || r.paymentIntent.status === "requires_payment_method") {
                    showErr("Bank account not linked. Please try again."); return;
                  }
                  submitEl.textContent = "Confirming…";
                  stripe.confirmUsBankAccountPayment(res.client_secret).then(function(cr) {
                    if (cr.error) showErr(cr.error.message); else showSuccess();
                  });
                });
              }
            });
          }).catch(function() { showErr("Something went wrong. Please try again."); });
        };

        modal.hidden = false;
      });
    });

    function showSuccess() {
      var s = section.querySelector("#rgl-fund-success");
      var sub = section.querySelector("#rgl-fund-submit");
      var pm = section.querySelector("#rgl-pay-methods");
      var msg = section.querySelector("#rgl-fund-msg");
      var cls = section.querySelector("#rgl-fund-modal-close");
      if (s)   s.style.display = "";
      if (sub) sub.style.display = "none";
      if (pm)  pm.style.display = "none";
      if (msg) msg.style.display = "none";
      if (cls) cls.style.display = "none";
      var successClose = section.querySelector("#rgl-fund-success-close");
      if (successClose) successClose.onclick = function() {
        section.querySelector("#rgl-fund-modal").hidden = true;
      };
    }

    section.querySelector("#rgl-fund-modal-close").addEventListener("click", function() {
      section.querySelector("#rgl-fund-modal").hidden = true;
    });
  }

  // ── Main render ───────────────────────────────────────────────────────────
  function render(root) {
    const { escapeHtml, formatDate } = window.BirchRegistry;
    const r = state.registry;

    const displayName = [r.registrantFirst, r.partnerFirst].filter(Boolean).join(" & ");
    const allItems   = r.items || [];
    const stillItems = allItems.filter((it) => it.stillNeeded > 0);
    const boughtItems = allItems.filter((it) => it.stillNeeded === 0 && it.quantityPurchased > 0);

    const totalWanted    = allItems.reduce((n, it) => n + (it.quantityWanted || 1), 0);
    const totalPurchased = allItems.reduce((n, it) => n + (it.quantityPurchased || 0), 0);
    const pct = totalWanted ? Math.round((totalPurchased / totalWanted) * 100) : 0;

    const dueDisplay = r.dueDate
      ? formatDate(r.dueDate)
      : "";

    root.innerHTML = `
    <div class="rgl-page">
      <!-- ── HERO (full-width) ── -->
      <section class="rgl-hero">
        <div class="rgl-hero__left">
          <div class="rgl-hero__eyebrow">${escapeHtml((r.name || "").toUpperCase())}</div>
          <h1 class="rgl-hero__heading">A new little life<br>is on the way.</h1>
          <p class="rgl-hero__lede">
            We&rsquo;re so grateful you&rsquo;re here and can&rsquo;t wait to share this journey with you. Every gift means the world to us. Thank you for being part of it.
          </p>
          <p class="rgl-hero__signature">
            <span class="rgl-hero__love">With love,</span>
            <span class="rgl-hero__names">${escapeHtml(displayName || "The Family")}</span>
          </p>
          <div class="rgl-hero__cta">
            <a class="registry-btn registry-btn--primary rgl-hero__shop-btn" href="#rgl-items">
              Shop the Registry
            </a>
          </div>
        </div>
        <div class="rgl-hero__right">
          <div class="rgl-progress-card">
            <div class="rgl-progress-card__icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
            </div>
            <div class="rgl-progress-card__label">Registry Progress</div>
            <div class="rgl-progress-bar-wrap">
              <div class="rgl-progress-bar" style="width:${pct}%"></div>
            </div>
            <div class="rgl-progress-card__stat">
              ${totalPurchased} of ${totalWanted} item${totalWanted !== 1 ? "s" : ""} purchased
            </div>
            ${dueDisplay ? `<div class="rgl-progress-card__due">Due ${escapeHtml(dueDisplay)}</div>` : ""}
          </div>
          ${r.imageUrl
            ? `<img class="rgl-hero__img" src="${escapeHtml(r.imageUrl)}" alt="" loading="eager">`
            : `<img class="rgl-hero__img" src="https://cdn.shopify.com/s/files/1/0675/0761/7858/files/RegsitryCreatePage.png?v=1778562114" alt="" loading="eager">`
          }
        </div>
      </section>

      <!-- ── ITEM FILTERS ── -->
      <div class="rgl-body">
      <section class="rgl-items-section" id="rgl-items">
        <div class="rgl-items-header">
          <div class="rgl-items-header__left">
            <div class="rgl-items-eyebrow">Choose something they'll love</div>
            <div class="rgl-filter-tabs" role="group" aria-label="Filter registry items">
              <button class="rgl-filter-tab ${state.filter === "all" ? "rgl-filter-tab--active" : ""}" data-filter="all">
                All Items (${allItems.length + state.funds.length})
              </button>
              <button class="rgl-filter-tab ${state.filter === "still_needed" ? "rgl-filter-tab--active" : ""}" data-filter="still_needed">
                Still Needed (${stillItems.length})
              </button>
              <button class="rgl-filter-tab ${state.filter === "purchased" ? "rgl-filter-tab--active" : ""}" data-filter="purchased">
                Purchased (${boughtItems.length})
              </button>
              <button class="rgl-filter-tab" data-filter="funds" style="${state.funds.length === 0 ? "display:none" : ""}">
                Funds (${state.funds.length})
              </button>
            </div>
          </div>
          <div class="rgl-sort-wrap">
            <label class="rgl-sort-label" for="rgl-sort">Sort by:</label>
            <select class="rgl-sort-select" id="rgl-sort">
              <option value="priority" ${state.sort === "priority" ? "selected" : ""}>Priority</option>
              <option value="price_asc" ${state.sort === "price_asc" ? "selected" : ""}>Price: Low to High</option>
              <option value="price_desc" ${state.sort === "price_desc" ? "selected" : ""}>Price: High to Low</option>
            </select>
          </div>
        </div>
        <div class="rgl-ship-pref">
          <span class="rgl-ship-pref__label">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
            ${state.shipToSelf
              ? "Your gift ships to your address"
              : `Your gift ships to ${escapeHtml(r.registrantFirst || displayName || r.name || "the family")}'s address`}
          </span>
          <button class="rgl-ship-pref__btn" data-ship-toggle>
            ${state.shipToSelf ? "Ship to registrant's address instead" : "Ship to my address instead"}
          </button>
        </div>

        <div class="rgl-grid" id="rgl-grid">
          ${renderGrid(r, displayName)}
        </div>
      </section>

      <!-- ── CASH FUNDS (below products) ── -->
      <div id="rgl-funds-section" class="rgl-funds-section" style="display:none"></div>

      <!-- ── WHY BIRCH ── -->
      <section class="rgl-why">
        <div class="rgl-why__left">
          <div class="rgl-why__eyebrow">Why parents love Birch Registry</div>
          <h2 class="rgl-why__heading">Thoughtful Gifts.<br>Real Impact.</h2>
        </div>
        <div class="rgl-why__features">
          <div class="rgl-feature">
            <div class="rgl-feature__icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </div>
            <h3 class="rgl-feature__title">Shop With Confidence</h3>
            <p class="rgl-feature__desc">Every item is evaluated for safety, quality, and real parent outcomes.</p>
          </div>
          <div class="rgl-feature">
            <div class="rgl-feature__icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
            </div>
            <h3 class="rgl-feature__title">Give More, Stress Less</h3>
            <p class="rgl-feature__desc">See exactly what's still needed so your gift lands at the perfect moment.</p>
          </div>
          <div class="rgl-feature">
            <div class="rgl-feature__icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </div>
            <h3 class="rgl-feature__title">Add a Personal Touch</h3>
            <p class="rgl-feature__desc">Send a heartfelt message that goes straight to the parents.</p>
          </div>
        </div>
      </section>

      <!-- ── PARENT MESSAGE ── -->
      <section class="rgl-message-band">
        <div class="rgl-message-band__img-wrap">
          ${r.imageUrl
            ? `<img src="${escapeHtml(r.imageUrl)}" alt="" class="rgl-message-band__img" loading="lazy">`
            : `<img src="https://cdn.shopify.com/s/files/1/0675/0761/7858/files/RegsitryCreatePage.png?v=1778562114" alt="" class="rgl-message-band__img" loading="lazy">`}
        </div>
        <div class="rgl-message-band__body">
          <div class="rgl-message-band__eyebrow">A message from ${escapeHtml(displayName || "the family")}</div>
          <p class="rgl-message-band__greeting">Hi friends and family,</p>
          <blockquote class="rgl-message-band__quote">
            We&rsquo;re so excited to welcome our little one and feel so lucky to have you in our village.
            If you&rsquo;d like to help us prepare, we&rsquo;ve created a registry with things we truly love and will use every day.
            Thank you so much for your love and support!
          </blockquote>
          <p class="rgl-message-band__sign">With love,</p>
          <p class="rgl-message-band__signature">${escapeHtml(displayName || "the family")}</p>
        </div>
      </section>

      <!-- ── HELP FOOTER ── -->
      <section class="rgl-help">
        <div class="rgl-help__icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        </div>
        <div class="rgl-help__body">
          <div class="rgl-help__title">Need Help?</div>
          <p class="rgl-help__desc">Questions about the registry or a product? We're here to help.</p>
          <div class="rgl-help__links">
            <a href="/pages/contact" class="rgl-help__link">Contact Support</a>
            <span class="rgl-help__sep" aria-hidden="true">·</span>
            <a href="/pages/how-birch-works" class="rgl-help__link">How Birch Works</a>
          </div>
        </div>
      </section>
      </div><!-- /.rgl-body -->
    </div>
    `;

    // Wire ship-to toggle
    root.querySelector("[data-ship-toggle]")?.addEventListener("click", () => {
      state.shipToSelf = !state.shipToSelf;
      render(root);
      setTimeout(() => root.querySelector("#rgl-items")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    });

    // Wire filter tabs
    root.querySelectorAll("[data-filter]").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.dataset.filter === "funds") {
          const fundsEl = document.getElementById("rgl-funds-section");
          if (fundsEl) fundsEl.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        state.filter = btn.dataset.filter;
        render(root);
        setTimeout(() => root.querySelector("#rgl-items")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      });
    });

    // Wire sort
    root.querySelector("#rgl-sort")?.addEventListener("change", (e) => {
      state.sort = e.target.value;
      root.querySelector("#rgl-grid").innerHTML = renderGrid(r, displayName);
      wireItemButtons(root, r);
      root.querySelector("[data-ship-toggle]")?.addEventListener("click", () => {
        state.shipToSelf = !state.shipToSelf;
        render(root);
      });
    });

    // Wire buy buttons
    wireItemButtons(root, r);

    // Smooth scroll for "Shop the Registry"
    root.querySelector(".rgl-hero__shop-btn")?.addEventListener("click", (e) => {
      const target = document.getElementById("rgl-items");
      if (target) { e.preventDefault(); target.scrollIntoView({ behavior: "smooth" }); }
    });

    // Restore funds section if already loaded (render() wipes the container)
    if (state.funds.length > 0) {
      const cfg = window.BirchCashFundsConfig || {};
      renderFundsSection(state.funds, cfg.aiProxy || "/apps/ai", cfg.stripePk);
    }
  }

  // ── Grid rendering ────────────────────────────────────────────────────────
  function renderGrid(r, displayName) {
    const { escapeHtml } = window.BirchRegistry;
    // Defensive dedupe by item id. The giver page was observed rendering
    // items with contributions twice; couldn't isolate the source by reading
    // code, so dedupe here while we instrument. If the upstream payload is
    // the source, the console.warn below surfaces it once per page load —
    // copy the log to debug.
    const seen = new Set();
    const dupes = [];
    let items = [];
    for (const it of r.items || []) {
      if (!it || !it.id) continue;
      if (seen.has(it.id)) { dupes.push(it.id); continue; }
      seen.add(it.id);
      items.push(it);
    }
    if (dupes.length && !window.__rglDupeLogged) {
      window.__rglDupeLogged = true;
      console.warn("[registry-giver] payload contained duplicate item ids", dupes);
    }

    // Filter
    switch (state.filter) {
      case "still_needed": items = items.filter((it) => it.stillNeeded > 0); break;
      case "purchased":    items = items.filter((it) => it.stillNeeded === 0 && it.quantityPurchased > 0); break;
    }

    // Sort
    switch (state.sort) {
      case "price_asc":  items.sort((a, b) => (a.priceCents ?? 0) - (b.priceCents ?? 0)); break;
      case "price_desc": items.sort((a, b) => (b.priceCents ?? 0) - (a.priceCents ?? 0)); break;
      case "priority":
      default: {
        const PRIO = { must_have: 0, nice_to_have: 1, optional: 2 };
        items.sort((a, b) => (PRIO[a.priority] ?? 1) - (PRIO[b.priority] ?? 1));
      }
    }

    if (items.length === 0) {
      return `<div class="rgl-empty">No items match this filter.</div>`;
    }

    // `recipientName` is the human label used in card copy ("Ships to <X>'s
    // address"). `registrantFirst` is a theme-only convention; not a real
    // DB column, so it's almost always undefined. Fall back through the
    // computed `displayName` (registrant + partner), then the registry
    // name (always set), then a generic copy phrase. This is the same
    // chain the ship-pref banner above uses — keep them in lockstep.
    const recipientName = r.registrantFirst || displayName || r.name || "the family";
    return items.map((it) => renderCard(it, displayName, recipientName)).join("");
  }

  function renderCard(it, displayName, registrantFirst) {
    const { escapeHtml, formatMoney } = window.BirchRegistry;
    const purchased = it.stillNeeded === 0;
    const isMustHave = it.priority === "must_have";
    // Top Pick banner hidden for now — falls through to the "Still Needed" badge.
    // Re-enable by restoring: const isTopPick = isMustHave;
    const isTopPick = false;

    let badgeHtml;
    if (purchased) {
      badgeHtml = `<span class="rgl-badge rgl-badge--purchased">
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        Purchased</span>`;
    } else if (it.groupGivingEnabled && it.pool) {
      const poolPct = it.pool.fundingPercentage || 0;
      badgeHtml = `<span class="rgl-badge rgl-badge--group">
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
        Group Gift · ${poolPct}% funded</span>`;
    } else if (isTopPick) {
      badgeHtml = `<span class="rgl-badge rgl-badge--top-pick">
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        Top Pick</span>`;
    } else {
      badgeHtml = `<span class="rgl-badge rgl-badge--needed">
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
        Still Needed</span>`;
    }

    // TrustScore hidden on the gift-giver page for now.
    // Re-enable by restoring: const trustHtml = renderTrustScore(it.trustScore);
    const trustHtml = "";

    // Parent "Why they picked it" note callout is intentionally not shown on the
    // gift-giver page. (The note still lives on the item for the registrant's own
    // views; we just don't surface it to guests.)
    const noteHtml = "";

    // Unified gift cart: both own-store and external items use "Add to cart"
    // as the single primary action. They check out together in one Shopify
    // order; external lines are fulfilled by the Birch agent team behind the
    // scenes (§ 7 / Lane 2D). The CTA reflects whether it's already in the cart.
    const inCart = state.cart?.has(it.id);
    const btnLabel = inCart ? "In cart ✓" : "Add to cart";

    // CUR-39: click the image or title to open the shared detail popup
    // (description + variant specs, read-only — no picker, no retailer link).
    // data-detail-* mirrors the descriptor registry-detail-popup.js's
    // descriptorFromCard() reads; data-card-detail marks the clickable targets.
    const detailAttrs = `
      data-detail-source="${escapeHtml(it.source)}"
      data-detail-title="${escapeHtml(it.title)}"
      data-detail-image="${escapeHtml(it.imageUrl || "")}"
      data-detail-price-cents="${it.priceCents ?? ""}"
      data-detail-currency="${escapeHtml(it.currency || "USD")}"
      data-detail-brand="${escapeHtml(it.brand || "")}"
      data-detail-handle="${escapeHtml(it.shopifyHandle || "")}"
      data-detail-url="${escapeHtml(it.externalUrl || "")}"
      data-detail-llid="${escapeHtml(it.lushlifeProductId || "")}"
      data-detail-retailer="${escapeHtml(it.externalRetailer || "")}"
    `;

    return `
      <article class="rgl-card ${purchased ? "rgl-card--purchased" : ""}" data-item-id="${escapeHtml(it.id)}" ${detailAttrs}>
        <div class="rgl-card__img-wrap">
          ${badgeHtml}
          ${it.imageUrl
            ? `<img class="rgl-card__img" src="${escapeHtml(it.imageUrl)}" alt="${escapeHtml(it.title)}" loading="lazy" data-card-detail>`
            : `<div class="rgl-card__img-placeholder" aria-hidden="true" data-card-detail></div>`}
          ${trustHtml}
        </div>
        <div class="rgl-card__body">
          <h3 class="rgl-card__title" data-card-detail>${escapeHtml(it.title)}</h3>
          <div class="rgl-card__price-row">
            <div class="rgl-card__price">
              ${it.priceCents != null ? formatMoney(it.priceCents, it.currency) : ""}
            </div>
            ${!purchased && it.stillNeeded > 1
              ? `<span class="rgl-card__needed" title="${it.stillNeeded} still needed">${it.stillNeeded} still needed</span>`
              : ""}
          </div>
          ${purchased
            ? `<div class="rgl-card__purchased-note">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Purchased ${it.purchasedAt ? formatRelativeDate(it.purchasedAt) : "recently"}
               </div>`
            : ""}
          ${noteHtml}
          ${purchased
            ? `<button class="registry-btn registry-btn--primary rgl-card__btn" disabled>Already gifted</button>`
            : (it.groupGivingEnabled && it.pool)
              ? renderGroupGiftCta(it)
              : `<button
                  class="registry-btn registry-btn--primary rgl-card__btn"
                  data-buy-item="${escapeHtml(it.id)}"
                  data-source="${escapeHtml(it.source)}"
                  data-variant-id="${escapeHtml(it.shopifyVariantId || "")}"
                  data-handle="${escapeHtml(it.shopifyHandle || "")}"
                  data-external-url="${escapeHtml(it.externalUrl || "")}"
                  data-price-cents="${it.priceCents ?? 0}"
                  data-item-title="${escapeHtml(it.title)}">
                  ${btnLabel}
                </button>`}
        </div>
      </article>
    `;
  }

  function renderTrustScore(score) {
    if (score == null || score === "") return "";
    const n = Number(score);
    if (!Number.isFinite(n) || n <= 0) return "";
    const v = Math.max(0, Math.min(100, Math.round(n)));
    return `
      <div class="rgl-trust" style="--trust-score-value: ${v};" aria-label="Birch TrustScore ${v}">
        <svg class="rgl-trust__ring" viewBox="0 0 40 40" aria-hidden="true">
          <circle class="rgl-trust__track" cx="20" cy="20" r="16" pathLength="100"></circle>
          <circle class="rgl-trust__arc" cx="20" cy="20" r="16" pathLength="100" style="stroke-dashoffset:${100 - v}"></circle>
        </svg>
        <span class="rgl-trust__val">${v}</span>
        <span class="rgl-trust__label">TrustScore</span>
      </div>`;
  }

  // ── Button wiring ─────────────────────────────────────────────────────────
  function wireItemButtons(root, _r) {
    root.querySelectorAll("[data-buy-item]").forEach((btn) => {
      if (!btn.disabled) {
        btn.addEventListener("click", () => onBuyClick(btn));
      }
    });
    root.querySelectorAll("[data-contribute-pool]").forEach((btn) => {
      btn.addEventListener("click", () => onContributeClick(btn, false));
    });
    root.querySelectorAll("[data-open-cart]").forEach((btn) => {
      btn.addEventListener("click", () => state.cartUI?.open());
    });
    // CUR-39: image/title open the read-only detail popup — no hooks, so the
    // shared component stays in detail-only mode (no variant picker, no
    // add/remove); hideExternalLink additionally drops the "View at
    // {retailer}" link for external items. See registry-detail-popup.js.
    root.querySelectorAll("[data-card-detail]").forEach((el) => {
      el.addEventListener("click", () => {
        const card = el.closest(".rgl-card");
        if (card && window.BirchDetailPopup) {
          window.BirchDetailPopup.openFromCard(card, { hideExternalLink: true });
        }
      });
    });
  }

  // Add-to-cart is the single primary action for every item (own-store AND
  // external). Both land in the same Birch gift cart (shared giftCartUI) and
  // check out together in one Shopify order. External lines are priced retailer
  // + 3% + shipping; own-store lines are plain. The server re-derives all
  // pricing authoritatively at /api/checkout-cart.
  function onBuyClick(btn) {
    const { showToast } = window.BirchRegistry;
    const itemId = btn.dataset.buyItem;
    const item = (state.registry?.items || []).find((x) => x.id === itemId);
    if (!item) return;

    // Own-store items need either a variant id OR a handle the server can
    // resolve to one at checkout (AI/chat-added items often have only a handle).
    // Block only when truly unlinked — neither present.
    if (item.source !== "external") {
      const variantId = (btn.dataset.variantId || "").trim();
      const handle = (btn.dataset.handle || "").trim();
      if (!variantId && !handle) {
        showToast("This item can't be checked out directly. Please ask the registrant to relink it.");
        return;
      }
    }

    state.cartUI.addItem(item);
  }

  // ── Group gifting — pool CTA render ──────────────────────────────────────
  function renderGroupGiftCta(it) {
    const { escapeHtml, formatMoney, poolLockNote } = window.BirchRegistry;
    const pool = it.pool;
    const contributed = pool.totalContributed || 0;
    const target = pool.targetAmount || 0;
    const remaining = Math.max(0, target - contributed);
    const pct = target > 0 ? Math.min(100, Math.round((contributed / target) * 100)) : 0;
    const count = pool.contributorCount || 0;
    const isFunded = ["funded", "redeemed", "partially_redeemed"].includes(pool.status);
    // Not full, but the family has locked it (paying a top-up, or converted
    // to credit): checkout would refuse a contribution, so don't offer one.
    const lockNote = poolLockNote(pool.status);
    const itemType = pool.itemType || (it.source === "external" ? "external" : "native");
    const feeRate = SERVICE_FEE_ENABLED ? (pool.feeRate != null ? pool.feeRate : (itemType === "external" ? 0.03 : 0)) : 0;

    // If a contribution toward this pool is already staged in the gift cart,
    // surface that instead of the contribute buttons (the amount is edited in
    // the cart drawer alongside every other gift).
    const staged = state.cart?.get ? state.cart.get(it.id) : null;
    const stagedContribCents = staged && staged.kind === "contribution" ? (staged.contributionCents || 0) : 0;

    return `
      <div class="rgl-pool">
        <div class="rgl-pool__bar-wrap"><div class="rgl-pool__bar" style="width:${pct}%"></div></div>
        <div class="rgl-pool__meta">
          ${count > 0 ? `<span class="rgl-pool__funded-label">${formatMoney(Math.round(contributed * 100))} of ${formatMoney(Math.round(target * 100))}</span>` : ""}
          <span class="rgl-pool__count">${count > 0 ? `${count} ${count === 1 ? "person" : "people"} chipped in` : lockNote ? "" : "Be the first to contribute"}</span>
        </div>
        ${"" /* 3% service-fee note hidden for now — re-enable: feeRate > 0 ? `<p class="rgl-pool__fee-note">3% service fee — secure & transparent.</p>` : "" */}
        ${isFunded
          ? `<div class="rgl-pool__funded-badge">
               <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
               Fully funded — thank you!
             </div>`
          : lockNote
            ? `<p class="rgl-pool__lock-note">${escapeHtml(lockNote)}</p>`
          : stagedContribCents > 0
            ? `<div class="rgl-pool__btns">
                 <button class="registry-btn registry-btn--primary rgl-pool__btn" data-open-cart="1">
                   In cart ✓ · ${formatMoney(stagedContribCents)}
                 </button>
               </div>`
            : `<div class="rgl-pool__btns">
               <button class="registry-btn registry-btn--primary rgl-pool__btn"
                 data-contribute-pool="${escapeHtml(pool.id)}"
                 data-item-title="${escapeHtml(it.title)}"
                 data-item-type="${itemType}"
                 data-fee-rate="${feeRate}"
                 data-target="${target.toFixed(2)}"
                 data-contributed="${contributed.toFixed(2)}"
                 data-remaining="${remaining.toFixed(2)}">
                 Contribute to gift
               </button>
             </div>`}
      </div>`;
  }

  // ── Group gifting — contribution flow ─────────────────────────────────────
  // Contributions now flow through the SAME unified gift cart as every other
  // gift: pick an amount, add it as a contribution line, and check out together
  // in one Shopify order (server: proxy.api.checkout-cart records the pending
  // RegistryGroupContribution + settles it on orders/paid). No separate
  // immediate-payment redirect, and identity (name/email/note) is collected once
  // at cart checkout rather than per contribution.
  async function onContributeClick(btn, buyAll) {
    const poolId = buyAll ? btn.dataset.buyallPool : btn.dataset.contributePool; // pool.id === item.id
    const item = (state.registry?.items || []).find((x) => x.id === poolId);
    if (!item) return;
    const remaining = parseFloat(btn.dataset.remaining) || 0;
    const itemType = btn.dataset.itemType || (item.source === "external" ? "external" : "native");
    const feeRate = SERVICE_FEE_ENABLED ? (parseFloat(btn.dataset.feeRate) || (itemType === "external" ? 0.03 : 0)) : 0;

    const cents = await window.BirchRegistry.openContributionAmountModal({
      itemTitle: btn.dataset.itemTitle || item.title || "this gift",
      itemType, feeRate, remaining, buyAll,
    });
    if (cents == null) return; // cancelled

    state.cartUI.addContribution({
      item,
      contributionCents: cents,
      remainingCents: Math.round(remaining * 100),
    });
  }

  // ── Utilities ─────────────────────────────────────────────────────────────
  function formatRelativeDate(dateStr) {
    try {
      const d = new Date(dateStr);
      const days = Math.round((Date.now() - d) / 86400000);
      if (days === 0) return "today";
      if (days === 1) return "yesterday";
      if (days < 7)  return `${days} days ago`;
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch (_) { return ""; }
  }

  function formatMoney(cents, currency) {
    return window.BirchRegistry.formatMoney(cents, currency);
  }

  function renderLoading() {
    return `<div class="rgl-loading"><span class="registry-loading"></span><p>Loading registry…</p></div>`;
  }

  // Password gate for a private registry. `info` carries only non-sensitive
  // teaser fields (displayName, imageUrl) — never items. On a correct password
  // the unlock cookie is set and we re-run init() to load the registry.
  function renderPasswordGate(root, slug, info) {
    const esc = (window.BirchRegistry?.escapeHtml) || ((s) => s);
    const name = info.displayName || "this registry";
    const noPassword = info.hasPassword === false; // owner hasn't finished setup
    root.innerHTML = `
      <div class="rgl-gate">
        ${info.imageUrl ? `<img class="rgl-gate__img" src="${esc(info.imageUrl)}" alt="">` : ""}
        <svg class="rgl-gate__lock" xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        <h2 class="rgl-gate__title">${esc(name)} is private</h2>
        ${noPassword
          ? `<p class="rgl-gate__sub">This registry isn't open for viewing yet. Please check back with the family.</p>`
          : `<p class="rgl-gate__sub">Enter the password the family shared with you to view their registry.</p>
        <form class="rgl-gate__form" novalidate>
          <input type="password" name="password" class="rgl-gate__input" autocomplete="off" placeholder="Password" aria-label="Registry password" required>
          <div class="rgl-gate__error" hidden></div>
          <button type="submit" class="registry-btn registry-btn--primary rgl-gate__btn">View registry</button>
        </form>`}
      </div>`;

    const form = root.querySelector(".rgl-gate__form");
    if (!form) return;
    const input = form.querySelector("[name=password]");
    const errEl = form.querySelector(".rgl-gate__error");
    const btn = form.querySelector(".rgl-gate__btn");
    setTimeout(() => input?.focus(), 0);

    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const password = (input.value || "").trim();
      if (!password) { input.focus(); return; }
      errEl.hidden = true;
      const label = btn.textContent;
      btn.disabled = true;
      btn.innerHTML = '<span class="registry-loading" style="width:16px;height:16px;border-width:2px"></span>';
      try {
        await window.BirchRegistry.API.unlockRegistry(slug, password);
        // Cookie is set — reload the real registry through the normal init path.
        await init(root);
      } catch (e) {
        btn.disabled = false;
        btn.textContent = label;
        errEl.textContent = e.status === 429
          ? "Too many attempts. Please wait a few minutes and try again."
          : e.status === 401
            ? "That password didn't match. Please try again."
            : "Something went wrong. Please try again.";
        errEl.hidden = false;
        input.select();
      }
    });
  }

  function renderError(msg) {
    const esc = (window.BirchRegistry?.escapeHtml) || ((s) => s);
    return `
      <div class="rgl-error">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v10H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
        <h2>${esc(msg)}</h2>
        <p>If you received a shared link, ask the family to confirm it's published.</p>
      </div>`;
  }
})();
